import React, { useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, IconButton, Tooltip,
} from '@mui/material';
import {
  Settings as PropertiesIcon,
  CheckCircle as ValidationIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import ValidationPanel from './ValidationPanel';

function PropertiesValidationPanel({
  // Properties props
  selectedNode,
  onNodeConfigUpdate,
  onNodeDelete,
  nodes,
  edges,
  globalState,

  // Validation props
  onNodeClick,
  onHighlightNodes,

  // Panel state
  collapsed,
  onToggleCollapse,
}) {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (collapsed) {
    return (
      <Box sx={{
        width: 40,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        pt: 1,
      }}
      >
        <Tooltip title="Expand Properties & Validation">
          <IconButton onClick={onToggleCollapse} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Paper sx={{
      maxWidth: 320,
      width: '100%',
      minWidth: 200,
      position: 'relative',
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0, // Prevent shrinking
    }}
    >
      {/* Header with Tabs */}
      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
        }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ minHeight: 'auto' }}
          >
            <Tab
              icon={<PropertiesIcon />}
              label="Properties"
              iconPosition="start"
              sx={{
                minHeight: 40,
                fontSize: '0.75rem',
                '& .MuiTab-iconWrapper': {
                  marginRight: 0.5,
                },
              }}
            />
            <Tab
              icon={<ValidationIcon />}
              label="Validation"
              iconPosition="start"
              sx={{
                minHeight: 40,
                fontSize: '0.75rem',
                '& .MuiTab-iconWrapper': {
                  marginRight: 0.5,
                },
              }}
            />
          </Tabs>

          <Tooltip title="Collapse Panel">
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{ ml: 1 }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{
        flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column',
      }}
      >
        {activeTab === 0 && (
          <Box sx={{
            flex: 1, overflow: 'auto', p: 2, pb: 6,
          }}
          >
            {/* Properties Content */}
            {selectedNode ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Node:
                  {' '}
                  {selectedNode.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Type:
                  {' '}
                  {selectedNode.type}
                </Typography>

                {selectedNode.type === 'start' ? (
                  <Typography variant="body2" color="text.secondary">
                    This is the starting point of your flow. All workflows begin here.
                  </Typography>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Configure this
                      {' '}
                      {selectedNode.type}
                      {' '}
                      node:
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <button
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                        onClick={() => onNodeConfigUpdate(selectedNode)}
                      >
                        Edit Configuration
                      </button>

                      <button
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#d32f2f',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                        }}
                        onClick={() => onNodeDelete(selectedNode.id)}
                      >
                        Delete Node
                      </button>
                    </Box>

                    {/* Show configuration summary */}
                    {selectedNode.data?.config && Object.keys(selectedNode.data.config || {}).length > 0 && (
                      <Box sx={{
                        mt: 2,
                        p: 1,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                      }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Current Configuration:
                        </Typography>
                        {Object.entries(selectedNode.data.config || {}).slice(0, 3).map(([key, value]) => (
                          <Typography key={key} variant="caption" display="block">
                            {key}
                            :
                            {typeof value === 'string' && value.length > 20
                              ? `${value.substring(0, 20)}...`
                              : String(value)}
                          </Typography>
                        ))}
                        {Object.keys(selectedNode.data.config || {}).length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            ...and
                            {' '}
                            {Object.keys(selectedNode.data.config || {}).length - 3}
                            {' '}
                            more
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a node to view and edit its properties
                </Typography>

                {/* Help Section */}
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  mb: 2,
                }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    💡 How to Use
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • Drag nodes from palette to canvas
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • Double-click nodes to configure
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • Double-click edges to configure
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • Right-click for context menu
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Drag nodes to move them
                  </Typography>
                </Box>

                {/* Flow Statistics */}
                <Box sx={{
                  p: 2,
                  bgcolor: 'success.lighter',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'success.light',
                }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                    📊 Flow Statistics
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Nodes:
                    {' '}
                    {nodes?.length || 0}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Connections:
                    {' '}
                    {edges?.length || 0}
                  </Typography>
                  <Typography variant="caption" display="block">
                    State Variables:
                    {' '}
                    {Object.keys(globalState || {}).length}
                  </Typography>

                  {/* Active Connections */}
                  {edges?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        🔗 Active Connections:
                      </Typography>
                      {edges.map((edge, index) => (
                        <Typography key={edge.id} variant="caption" display="block">
                          {index + 1}
                          .
                          {edge.source}
                          {' '}
                          →
                          {edge.target}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* Validation Content */}
            <ValidationPanel
              nodes={nodes}
              edges={edges}
              globalState={globalState}
              onNodeClick={onNodeClick}
              onHighlightNodes={onHighlightNodes}
              collapsed={false}
              onToggleCollapse={() => {}}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default PropertiesValidationPanel;
